/* -----------------Globals--------------- */
import { useCallback } from "react";
import { Platform } from "react-native";

import { currentAppVersion } from "@helpers/expo-extras";
/* -----------------Helpers & Hooks--------------- */
import i18n from "@helpers/lib/i18n";
import { trackCustomEvent } from "analytics";
import useSessionStore from "./useSessionStore";

/* -----------------Types--------------- */
import type { FeedbackParams } from "@memorang/features";

import { useExamContext } from "@features/exam";
import useAppConfigIds from "@hooks/useAppConfigIds";
/* -----------------Contexts--------------- */
import { useAppStore } from "@hooks/useAppStore";
import useClerkUser from "@hooks/useClerkUser";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import { useAuthContext } from "@memorang/applets";
import { useAilaStore } from "./useAilaStore";

export const useQASearch = () => {
	const { viewerId } = useAuthContext();
	const { email } = useClerkUser();
	const {
		tenant: { name: tenantName },
	} = useAppStore((store) => ({
		app: store.app,
		tenant: store.tenant,
	}));
	const itemId = useAilaStore((state) => state.itemId);
	const sessionId = useSessionStore((state) => state.id);
	const inReviewMode = useSessionStore((state) => state.inReviewMode);
	const isSummativeTest = useSessionStore((state) => state.isSummativeTest);
	const examId = tenantName; // TODO: remove this once we have the real data

	const { projectId, organizationId, productId } = useAppConfigIds();

	const {
		currentExamContext: {
			studyPackProductId,
			practiceTestProductId,
			// examId,
		},
	} = useExamContext();

	const examName = useDemoExamStore((store) => store.currentExam?.name);

	const handleTracking = useCallback(
		(eventName: string, data?: Record<string, string>) => {
			const viewForFormative = inReviewMode
				? "review_session_formative"
				: "session_formative";
			trackCustomEvent({
				eventName,
				...(data || {}),
				view: isSummativeTest ? "review_session_summative" : viewForFormative,
			});
		},
		[inReviewMode, isSummativeTest],
	);

	const type = practiceTestProductId ? "Practice Test" : "Study-Pack";
	const contextId = practiceTestProductId ?? studyPackProductId;

	const feedbackDialogParams: FeedbackParams = {
		email: email!,
		tenantId: tenantName,
		sessionId: `${sessionId}`,
		appLanguage: i18n.language,
		platform: Platform.OS.toUpperCase(),
		currentAppVersion,
		view: isSummativeTest
			? "Summative Review Session"
			: "Formative Review Session",
		context: examName,
		product: `${examName} ${type}`,
		feature: "AI QA",
		projectId,
		organizationId,
		productId,
		itemId,
		itemVersionId: "1",
		sessionType: "FORMATIVE",
	};

	return {
		viewerId,
		examId,
		contextId,
		handleTracking,
		feedbackDialogParams,
		examName,
	};
};
